/**
 * What the admin page shows about a stored platform error.
 *
 * THE SUMMARY IS BUILT, NOT FILTERED. Steps 1-8 kept raw error text out of the
 * store, out of the digest email and out of the browser's report. This page is
 * the last surface, and the easiest place to undo all of it — the rows arrive
 * from Firestore as plain objects, so rendering one wholesale would put study
 * content on screen and into any screenshot of it. Naming the fields to include
 * makes that impossible by construction, which is the same choice
 * `functions/src/errorTracking/errorTemplates.ts` makes when it renders the
 * digest sentence.
 */

export interface AdminErrorRow {
  fingerprint: string;
  errorClass?: string;
  callableName?: string;
  route?: string;
  projectId?: string;
  formCode?: string;
  reference?: string;
  count?: number;
  firstSeenAt?: string;
  lastSeenAt?: string;
  acknowledged?: boolean;
  acknowledgment?: {
    adjudication: string;
    acknowledgedBy: string;
    acknowledgedAt: string;
    note?: string;
  } | null;
  [k: string]: unknown;
}

/**
 * The three verdicts the endpoint accepts, with the sentence an operator needs
 * before clicking. `resolved`'s hint is load-bearing rather than decorative: it
 * is the one choice whose behaviour surprises people, because it deliberately
 * does NOT silence a recurrence.
 */
export const ERROR_ADJUDICATION_OPTIONS = [
  {
    value: 'known-issue-recorded',
    label: 'Known issue',
    hint: 'Real and understood. Stops appearing in the morning email until it happens again.',
  },
  {
    value: 'false-positive-recorded',
    label: 'Not a real error',
    hint: 'The capture is noise. Stops appearing until it happens again.',
  },
  {
    value: 'resolved',
    label: 'Fixed',
    hint: 'The cause was corrected. Keeps appearing if it happens again — a fix that comes undone should be loud.',
  },
] as const;

/** One line about an error, from named safe fields only. */
export function errorRowSummary(row: AdminErrorRow): string {
  const where = row.callableName || row.route;
  const parts: string[] = [row.errorClass || 'UNKNOWN'];

  if (where) parts.push(`on ${where}`);
  if (row.projectId) parts.push(`project ${row.projectId}`);
  if (row.formCode) parts.push(`form ${row.formCode}`);
  if (typeof row.count === 'number') {
    parts.push(row.count === 1 ? 'seen once' : `seen ${row.count} times`);
  }
  if (row.reference) parts.push(`log reference ${row.reference}`);

  return parts.join(' — ');
}

/**
 * Unjudged first, then most recent. A page that interleaved the two would bury
 * the thing needing attention under the things already dealt with.
 */
export function sortErrorRows<T extends AdminErrorRow>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const aAck = a.acknowledged ? 1 : 0;
    const bAck = b.acknowledged ? 1 : 0;
    if (aAck !== bAck) return aAck - bAck;
    return String(b.lastSeenAt ?? '').localeCompare(String(a.lastSeenAt ?? ''));
  });
}
